// ─── CSV Utility ────────────────────────────────────────────────
// Minimal utilities for CSV file validation and download.
// All CSV parsing/processing is handled by the backend.

// ─── Constants ──────────────────────────────────────────────────

export const EXPECTED_CSV_COLUMNS = [
	'serviceid',
	'servicename',
	'description',
	'status',
	'size',
	'length',
	'durationminutes',
	'price',
] as const;

// ─── Types ──────────────────────────────────────────────────────

export interface CsvValidationResult {
	valid: boolean;
	serviceCount: number;
	totalRows: number;
	error?: string;
}

// ─── CSV Line Parser ────────────────────────────────────────────

function parseCsvLine(line: string): string[] {
	const result: string[] = [];
	let current = '';
	let inQuotes = false;

	for (let i = 0; i < line.length; i++) {
		const ch = line[i];
		if (inQuotes) {
			if (ch === '"' && line[i + 1] === '"') {
				current += '"';
				i++;
			} else if (ch === '"') {
				inQuotes = false;
			} else {
				current += ch;
			}
		} else {
			if (ch === '"') {
				inQuotes = true;
			} else if (ch === ',') {
				result.push(current.trim());
				current = '';
			} else {
				current += ch;
			}
		}
	}
	result.push(current.trim());
	return result;
}

// ─── File Validation ────────────────────────────────────────────

export function validateServiceCsvFile(file: File): Promise<CsvValidationResult> {
	return new Promise(resolve => {
		if (!file.name.toLowerCase().endsWith('.csv')) {
			resolve({
				valid: false,
				serviceCount: 0,
				totalRows: 0,
				error: 'Please upload a CSV file.',
			});
			return;
		}

		const reader = new FileReader();
		reader.onload = e => {
			const text = e.target?.result as string;
			if (!text || text.trim().length === 0) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: 'CSV file is empty.',
				});
				return;
			}

			const lines = text
				.split('\n')
				.map(l => l.trim())
				.filter(l => l.length > 0);
			if (lines.length < 2) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: 'CSV file has no data rows.',
				});
				return;
			}

			// Check header
			const header = parseCsvLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, ''));
			const missing = EXPECTED_CSV_COLUMNS.filter(col => !header.includes(col));
			if (missing.length > 0) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: `Missing required columns: ${missing.join(', ')}`,
				});
				return;
			}

			// Count data rows and unique service names
			const serviceNameIdx = header.indexOf('servicename');
			const serviceNames = new Set<string>();
			let totalRows = 0;

			for (let i = 1; i < lines.length; i++) {
				const cells = parseCsvLine(lines[i]);
				const name = cells[serviceNameIdx]?.trim();
				if (name) {
					serviceNames.add(name.toLowerCase());
					totalRows++;
				}
			}

			if (totalRows === 0) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: 'No valid data rows found in CSV.',
				});
				return;
			}

			resolve({ valid: true, serviceCount: serviceNames.size, totalRows });
		};

		reader.onerror = () => {
			resolve({ valid: false, serviceCount: 0, totalRows: 0, error: 'Failed to read file.' });
		};

		reader.readAsText(file);
	});
}

// ─── Task CSV Validation ────────────────────────────────────────

export const EXPECTED_TASK_CSV_COLUMNS = [
	'servicetaskid',
	'serviceid',
	'servicename',
	'taskid',
	'taskname',
	'size',
	'length',
	'durationminutes',
	'price',
] as const;

export function validateTaskCsvFile(file: File): Promise<CsvValidationResult> {
	return new Promise(resolve => {
		if (!file.name.toLowerCase().endsWith('.csv')) {
			resolve({
				valid: false,
				serviceCount: 0,
				totalRows: 0,
				error: 'Please upload a CSV file.',
			});
			return;
		}

		const reader = new FileReader();
		reader.onload = e => {
			const text = e.target?.result as string;
			if (!text || text.trim().length === 0) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: 'CSV file is empty.',
				});
				return;
			}

			const lines = text
				.split('\n')
				.map(l => l.trim())
				.filter(l => l.length > 0);
			if (lines.length < 2) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: 'CSV file has no data rows.',
				});
				return;
			}

			const header = parseCsvLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, ''));
			const missing = EXPECTED_TASK_CSV_COLUMNS.filter(col => !header.includes(col));
			if (missing.length > 0) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: `Missing required columns: ${missing.join(', ')}`,
				});
				return;
			}

			const taskNameIdx = header.indexOf('taskname');
			const taskNames = new Set<string>();
			let totalRows = 0;

			for (let i = 1; i < lines.length; i++) {
				const cells = parseCsvLine(lines[i]);
				const taskName = cells[taskNameIdx]?.trim();
				if (taskName) {
					taskNames.add(taskName.toLowerCase());
					totalRows++;
				}
			}

			if (totalRows === 0) {
				resolve({
					valid: false,
					serviceCount: 0,
					totalRows: 0,
					error: 'No valid data rows found in CSV.',
				});
				return;
			}

			resolve({ valid: true, serviceCount: taskNames.size, totalRows });
		};

		reader.onerror = () => {
			resolve({ valid: false, serviceCount: 0, totalRows: 0, error: 'Failed to read file.' });
		};

		reader.readAsText(file);
	});
}

// ─── Download Helpers ────────────────────────────────────────────

/**
 * Trigger a browser download of CSV content (plain string from the API).
 */
export function downloadCsvString(csvContent: string, fileName: string): void {
	const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = fileName;
	a.click();
	URL.revokeObjectURL(url);
}
